import { useEffect, useState, type ChangeEvent, type FormEvent } from "react";
import { useSession } from "../lib/session";
import { Link, useParams } from "react-router";
import { ErrorNotice, useAction } from "../components/ui";
import { api, type ActivityKind, type Candidate } from "../lib/api";
import { describeEvent, EVENT_MARK } from "./ApplicationPage";
import { KIND, KIND_MARK } from "./CompanyCrm";
import { displaySkill, formatSalary } from "../lib/format";
import { DOCUMENT_TYPES, openDownload, uploadFile, uploadProblem } from "../lib/upload";
import { CandidateForm } from "./CandidateForm";
import { CandidateJobs } from "./CandidateJobs";
import { CandidatePools } from "./CandidatePools";
import { useTitle } from "../lib/title";

export function CandidatePage() {
  const { id = "" } = useParams();
  const [candidate, setCandidate] = useState<Candidate | null>(null);
  useTitle(candidate?.fullName ?? "Candidate");
  const [editing, setEditing] = useState(false);
  const [confirmErase, setConfirmErase] = useState(false);
  const { session } = useSession();
  const isOwner = session.status === "member" && session.me.user.role === "owner";
  const load = useAction();
  const act = useAction();
  const [flash] = useState(() => {
    const message = sessionStorage.getItem("flash");
    sessionStorage.removeItem("flash");
    return message;
  });

  useEffect(() => {
    void load.run(async () => setCandidate((await api.candidate(id)).candidate));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  if (!candidate) {
    return (
      <>
        <p className="label">
          <Link to="/candidates">← Candidates</Link>
        </p>
        <ErrorNotice error={load.error} />
        {!load.error && <p className="label">Loading…</p>}
      </>
    );
  }

  const role = [candidate.currentTitle, candidate.currentEmployer && `at ${candidate.currentEmployer}`].filter(Boolean).join(" ");

  const toggleStatus = () =>
    void act.run(async () =>
      setCandidate((await api.updateCandidate(candidate.id, { status: candidate.status === "active" ? "inactive" : "active" })).candidate),
    );

  const replaceCv = (e: ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;
    const problem = uploadProblem(file, DOCUMENT_TYPES);
    if (problem) return act.setError(problem);
    void act.run(async () => setCandidate((await api.setCandidateCv(candidate.id, (await uploadFile(file)).id)).candidate));
  };

  return (
    <>
      <div className="page-head">
        <p className="label">
          <Link to="/candidates">← Candidates</Link>
          {candidate.status === "inactive" && " · Inactive"}
        </p>
        <h1 className="display">{candidate.fullName}</h1>
        {role && <p className="lede">{role}</p>}
      </div>

      <ErrorNotice error={act.error ?? flash} />

      <section className="section" aria-labelledby="profile-h">
        <div className="section-head">
          <h2 id="profile-h" className="label">
            Profile
          </h2>
          <span className="button-row">
            {!editing && (
              <button type="button" className="link-btn" onClick={() => setEditing(true)}>
                Edit
              </button>
            )}
            <button type="button" className="link-btn" disabled={act.pending} onClick={toggleStatus}>
              {candidate.status === "active" ? "Mark inactive" : "Mark active"}
            </button>
          </span>
        </div>
        {editing ? (
          <CandidateForm
            candidate={candidate}
            submitLabel="Save"
            onCancel={() => setEditing(false)}
            onSaved={(c) => {
              setCandidate(c);
              setEditing(false);
            }}
          />
        ) : (
          <dl className="ledger">
            <div>
              <dt className="label">Contact</dt>
              <dd className="contact-links">
                {candidate.email && <a href={`mailto:${candidate.email}`}>{candidate.email}</a>}
                {candidate.phone && (
                  <a className="figures" href={`tel:${candidate.phone.replace(/[^\d+]/g, "")}`}>
                    {candidate.phone}
                  </a>
                )}
                {candidate.linkedinUrl && (
                  <a href={candidate.linkedinUrl} target="_blank" rel="noopener noreferrer">
                    LinkedIn ↗
                  </a>
                )}
                {!candidate.email && !candidate.phone && !candidate.linkedinUrl && "—"}
              </dd>
            </div>
            <div>
              <dt className="label">Location</dt>
              <dd>{candidate.location ?? "—"}</dd>
            </div>
            <div>
              <dt className="label">Expects</dt>
              <dd className="figures">{formatSalary(candidate.salaryExpectation, null, candidate.currency).replace("from ", "") || "—"}</dd>
            </div>
            <div>
              <dt className="label">Notice</dt>
              <dd>{candidate.noticePeriod ?? "—"}</dd>
            </div>
            <div className="wide">
              <dt className="label">Skills</dt>
              <dd className="chips">
                {candidate.skills.length ? candidate.skills.map((s) => <span key={s} className="chip">{displaySkill(s)}</span>) : "—"}
              </dd>
            </div>
            <div>
              <dt className="label">Source</dt>
              <dd>{candidate.source ?? "—"}</dd>
            </div>
            <div>
              <dt className="label">Owner</dt>
              <dd>{candidate.owner.name}</dd>
            </div>
          </dl>
        )}
      </section>

      <Timeline candidate={candidate} onSaved={setCandidate} />

      <CandidateJobs candidateId={candidate.id} />

      <CandidatePools candidateId={candidate.id} name={candidate.fullName} erased={candidate.erased} />

      <section className="section" aria-labelledby="cv-h">
        <div className="section-head">
          <h2 id="cv-h" className="label">
            CV
          </h2>
        </div>
        <p className="button-row spaced-top">
          {candidate.cv ? (
            <button type="button" className="btn secondary" onClick={() => void act.run(() => openDownload(candidate.cv!.id))}>
              Download {candidate.cv.filename}
            </button>
          ) : (
            <span className="empty">No CV yet.</span>
          )}
          <label className="link-btn file-btn">
            {candidate.cv ? "Upload a newer CV" : "Upload CV"}
            <input
              type="file"
              className="sr-only"
              accept=".pdf,.doc,.docx,application/pdf,application/msword,application/vnd.openxmlformats-officedocument.wordprocessingml.document"
              onChange={replaceCv}
            />
          </label>
          {act.pending && <span className="label">Working…</span>}
        </p>
      </section>

      <section className="section" aria-labelledby="gdpr-h">
        <div className="section-head">
          <h2 id="gdpr-h" className="label">
            Their data
          </h2>
          <span className="label">
            {candidate.lawfulBasis === "consent" ? "Consent" : "Legitimate interests"}
            {candidate.privacyNoticeSentOn ? (
              <> · notice sent {candidate.privacyNoticeSentOn}</>
            ) : (
              !candidate.erased && <span className="alert"> · no privacy notice recorded</span>
            )}
          </span>
        </div>
        <div className="button-row spaced-top">
          <button
            type="button"
            className="link-btn"
            disabled={act.pending}
            onClick={() =>
              void act.run(async () => {
                const data = await api.exportCandidate(candidate.id);
                const blob = new Blob([JSON.stringify(data, null, 2)], { type: "application/json" });
                const a = document.createElement("a");
                a.href = URL.createObjectURL(blob);
                a.download = `${candidate.fullName.replace(/[^a-z0-9]+/gi, "-").replace(/^-+|-+$/g, "").toLowerCase()}-data.json`;
                a.click();
                URL.revokeObjectURL(a.href);
              })
            }
          >
            Export everything we hold (JSON)
          </button>
          {isOwner && !candidate.erased && !confirmErase && (
            <button type="button" className="link-btn danger" onClick={() => setConfirmErase(true)}>
              Erase this person
            </button>
          )}
          {confirmErase && (
            <>
              <span className="label alert">
                Erase {candidate.fullName}? Their details, CVs and interview feedback go; placements stay for your accounts.
              </span>
              <button
                type="button"
                className="link-btn danger"
                disabled={act.pending}
                onClick={() =>
                  void act.run(async () => {
                    await api.eraseCandidate(candidate.id);
                    setConfirmErase(false);
                    setCandidate((await api.candidate(candidate.id)).candidate);
                  })
                }
              >
                Erase permanently
              </button>
              <button type="button" className="link-btn" onClick={() => setConfirmErase(false)}>
                Keep
              </button>
            </>
          )}
        </div>
        {candidate.erased && <p className="notice">This person was erased. Only the anonymised record remains, for linked placements.</p>}
      </section>
    </>
  );
}

const noteTime = new Intl.DateTimeFormat("en-GB", { day: "numeric", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit" });

/**
 * Everything logged about the candidate, newest first: calls, emails, meetings and notes, plus
 * comments left on their applications. Each entry keeps its time and author and can't be edited.
 */
function Timeline({ candidate, onSaved }: { candidate: Candidate; onSaved: (c: Candidate) => void }) {
  const [kind, setKind] = useState<ActivityKind>("note");
  const [body, setBody] = useState("");
  const { pending, error, run } = useAction();
  const submit = (e: FormEvent) => {
    e.preventDefault();
    void run(async () => {
      onSaved((await api.addCandidateNote(candidate.id, kind, body)).candidate);
      setBody("");
    });
  };
  return (
    <section className="section" aria-labelledby="timeline-h">
      <div className="section-head">
        <h2 id="timeline-h" className="label">
          Timeline
        </h2>
        {candidate.timeline.length > 0 && <span className="label figures">{candidate.timeline.length}</span>}
      </div>
      {!candidate.erased && (
        <form className="form narrow log-form" onSubmit={submit}>
          <fieldset className="segmented">
            <legend className="sr-only">Type</legend>
            {(Object.keys(KIND) as ActivityKind[]).map((k) => (
              <label key={k}>
                <input type="radio" name="note-kind" checked={kind === k} onChange={() => setKind(k)} />
                <span>{KIND[k]}</span>
              </label>
            ))}
          </fieldset>
          <label className="field">
            <span className="label">{kind === "note" ? "Note" : "What happened"}</span>
            <textarea rows={3} maxLength={20000} value={body} onChange={(e) => setBody(e.target.value)} />
          </label>
          <ErrorNotice error={error} />
          <div className="button-row">
            <button className="btn secondary" disabled={pending || !body.trim()}>
              {pending ? "Saving…" : kind === "note" ? "Add note" : `Log ${KIND[kind].toLowerCase()}`}
            </button>
            <span className="label">Saved with the time and your name. Entries can't be edited later.</span>
          </div>
        </form>
      )}
      <ol className="index timeline">
        {candidate.timeline.map((e) => (
          <li key={e.id}>
            <span className="num kind" aria-hidden>
              {e.source === "note" ? KIND_MARK[e.kind] : EVENT_MARK[e.kind]}
            </span>
            <span className="main">
              {e.source === "pipeline" && (
                <span className="primary">
                  {describeEvent(e)} · <Link to={`/applications/${e.application.id}`}>{e.application.jobTitle}, {e.application.companyName}</Link>
                </span>
              )}
              <span className={e.source === "note" ? "primary prewrap" : "prewrap"}>{e.body}</span>
              <span className="secondary">
                {e.source === "note" ? KIND[e.kind] : "Stage"} ·{" "}
                {e.author?.name ?? "Unknown author"} ·{" "}
                <time className="figures" dateTime={e.createdAt}>
                  {noteTime.format(new Date(e.createdAt))}
                </time>
              </span>
            </span>
            <span className="actions" />
          </li>
        ))}
        {candidate.timeline.length === 0 && (
          <li>
            <span className="empty">Nothing logged yet.</span>
          </li>
        )}
      </ol>
    </section>
  );
}
